// The poll of a job output saved to the library: the server copies it in the background and
// answers `pending` (S1), so the library's saving card, the Generate image dialog and the editor's
// open read the asset again until it is ready.
import { useQueryClient } from "@tanstack/react-query";

import { getGetAssetQueryKey } from "../../api/generated/endpoints/assets/assets";
import type { AssetStatus } from "../../api/generated/model";

/** How often a save that is still copying is read again, and for how many reads at most. */
export const SAVING_POLL_MS = 2_000;
export const SAVING_MAX_READS = 30;

/** The poll of a saved asset: every 2 s while it is pending, for about a minute in all. */
export function savingPollInterval(status: AssetStatus | undefined, reads: number): number | false {
  return status === "pending" && reads < SAVING_MAX_READS ? SAVING_POLL_MS : false;
}

/**
 * The reads of a saved asset's poll so far: its query's own count, the one `savingPollInterval`
 * stops on. TanStack Query renders a component again only for what it reads, and a last read that
 * answers as the one before changes no data, so this reads the query's `dataUpdatedAt`: the
 * caller renders again on every read and sees the poll end.
 */
export function useSavingReads(
  asset: { dataUpdatedAt: number },
  teamId: string,
  brandId: string,
  assetId: string,
): number {
  const queryClient = useQueryClient();
  if (asset.dataUpdatedAt === 0) return 0;
  return (
    queryClient.getQueryState(getGetAssetQueryKey(teamId, brandId, assetId))?.dataUpdateCount ?? 0
  );
}
